// src/components/ProtectedRoute.js
import React from 'react';
import { useAuth } from '../context/AuthContext';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { IoAlertCircleOutline } from 'react-icons/io5';
import { isModuleEnabled } from '../utils/saasConfig';

function RestrictedRouteModal() {
  const navigate = useNavigate();
  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.6)', backdropFilter: 'blur(4px)',
      display: 'flex', justifyContent: 'center', alignItems: 'center',
      zIndex: 99999,
      fontFamily: "'Inter', sans-serif"
    }}>
      <div style={{
        background: 'white', padding: '30px', borderRadius: '24px',
        width: '90%', maxWidth: '380px', textAlign: 'center',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.15), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        animation: 'permissionsFadeIn 0.3s ease-out'
      }}>
        <div style={{
          width: '60px', height: '60px', borderRadius: '50%',
          backgroundColor: '#fee2e2', display: 'flex',
          alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 20px', color: '#ef4444',
          boxShadow: '0 4px 10px rgba(239, 68, 68, 0.1)'
        }}>
          <IoAlertCircleOutline size={30} />
        </div>
        <h3 style={{ margin: '0 0 10px', fontSize: '1.3rem', fontWeight: '800', color: '#1e293b' }}>
          Acceso Restringido
        </h3>
        <p style={{ margin: '0 0 24px', fontSize: '0.95rem', color: '#64748b', lineHeight: '1.5' }}>
          No tienes permiso de tu titular para acceder a este módulo.
        </p>
        <button
          onClick={() => navigate('/', { replace: true })}
          style={{
            width: '100%', padding: '12px 24px', border: 'none',
            borderRadius: '12px', background: '#3b82f6', color: 'white',
            fontWeight: '700', fontSize: '0.95rem', cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(37, 99, 235, 0.25)',
            transition: 'all 0.2s'
          }}
        >
          Entendido
        </button>
      </div>
    </div>
  );
}

function ProtectedRoute() {
  // ¡Usamos el 'loading' de AuthContext!
  const { currentUser, userData, loading, isMoroso } = useAuth();

  const location = useLocation();
  const navigate = useNavigate();

  if (loading) {
    return <div className="form-container"><p>Cargando...</p></div>;
  }

  // 1. Si no hay usuario (currentUser), redirige a /login
  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  // 2. Si hay usuario pero NO datos (Google/FB) O requiere verificación (Test Users)
  // EVITAR BUCLE: Si ya estamos en /completar-registro, no redirigir
  if (currentUser && (!userData || userData.requiresVerification) && location.pathname !== '/completar-registro') {
    return <Navigate to="/completar-registro" replace />;
  }

  // 2.2 Si el usuario debe actualizar su contraseña obligatoriamente
  if (userData && userData.cambiarContrasena && location.pathname !== '/actualizar-contrasena') {
    return <Navigate to="/actualizar-contrasena" replace />;
  }

  // 2.5 Bloqueo de Morosos en Marketplace y Proveedor
  if (isMoroso && (location.pathname.startsWith('/marketplace') || location.pathname.startsWith('/proveedor'))) {
    return (
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)', backdropFilter: 'blur(4px)',
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        zIndex: 99999,
        fontFamily: "'Inter', sans-serif"
      }}>
        <div style={{
          background: 'white', padding: '30px', borderRadius: '24px',
          width: '90%', maxWidth: '380px', textAlign: 'center',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.15), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          animation: 'permissionsFadeIn 0.3s ease-out'
        }}>
          <div style={{
            width: '60px', height: '60px', borderRadius: '50%',
            backgroundColor: '#fee2e2', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 20px', color: '#ef4444',
            boxShadow: '0 4px 10px rgba(239, 68, 68, 0.1)'
          }}>
            <IoAlertCircleOutline size={30} />
          </div>
          <h3 style={{ margin: '0 0 10px', fontSize: '1.3rem', fontWeight: '800', color: '#1e293b' }}>
            Acceso Suspendido
          </h3>
          <p style={{ margin: '0 0 24px', fontSize: '0.95rem', color: '#64748b', lineHeight: '1.5' }}>
            Tienes adeudos pendientes. Por favor, realiza tu pago para habilitar este módulo.
          </p>
          <button
            onClick={() => navigate('/pagar-cuota', { replace: true })}
            style={{
              width: '100%', padding: '12px 24px', border: 'none',
              borderRadius: '12px', background: '#3b82f6', color: 'white',
              fontWeight: '700', fontSize: '0.95rem', cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(37, 99, 235, 0.25)',
              transition: 'all 0.2s'
            }}
          >
            Ir a Pagar Cuota
          </button>
        </div>
      </div>
    );
  }

  // 3. Bloqueo de Módulos (Tenants)
  const routePermissions = {
    '/reservar': 'reservar',
    '/accesos': 'accesos',
    '/estacionamientos': 'estacionamientos',
    '/vehiculos': 'vehiculos',
    '/pagar-cuota': 'cuotas',
    '/finanzas': 'finanzas'
  };

  const requiredPermission = Object.keys(routePermissions).find(path => location.pathname.startsWith(path));
  if (requiredPermission && userData?.tipoResidente === 'Inquilino' && userData?.rol !== 'vigilante') {
    const permKey = routePermissions[requiredPermission];
    const hasPerm = userData?.permisosInquilino && userData.permisosInquilino[permKey];
    if (!hasPerm) {
      return <RestrictedRouteModal />;
    }
  }

  // 3.5 SaaS Subscription lock check
  const isSaaSActive = isModuleEnabled(userData?.condoData, location.pathname);
  if (!isSaaSActive) {
    const isResident = userData?.rol === 'residente';
    return (
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)', backdropFilter: 'blur(4px)',
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        zIndex: 99999,
        fontFamily: "'Inter', sans-serif"
      }}>
        <div style={{
          background: 'white', padding: '30px', borderRadius: '24px',
          width: '90%', maxWidth: '400px', textAlign: 'center',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.15), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
        }}>
          <div style={{
            width: '60px', height: '60px', borderRadius: '50%',
            backgroundColor: '#e0e7ff', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 20px', color: '#4f46e5',
            boxShadow: '0 4px 10px rgba(79, 70, 229, 0.1)'
          }}>
            <IoAlertCircleOutline size={30} />
          </div>
          <h3 style={{ margin: '0 0 10px', fontSize: '1.25rem', fontWeight: '800', color: '#1e293b' }}>
            Módulo no contratado
          </h3>
          <p style={{ margin: '0 0 24px', fontSize: '0.92rem', color: '#64748b', lineHeight: '1.5' }}>
            {isResident 
              ? 'Esta funcionalidad no está activa para tu fraccionamiento. Sugiere su activación a tu comité desde la pantalla de inicio.'
              : 'Este módulo requiere una suscripción SaaS activa. Contáctanos desde tu panel de inicio para habilitarlo.'
            }
          </p>
          <button
            onClick={() => navigate('/', { replace: true })}
            style={{
              width: '100%', padding: '12px 24px', border: 'none',
              borderRadius: '12px', background: '#4f46e5', color: 'white',
              fontWeight: '700', fontSize: '0.95rem', cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(79, 70, 229, 0.25)',
              transition: 'all 0.2s'
            }}
          >
            Volver al Inicio
          </button>
        </div>
      </div>
    );
  }

  // 4. Si hay usuario Y datos, deja pasar
  return <Outlet />;
}

export default ProtectedRoute;